// R5.2 — the review surface's style constants (spec §1.4).
//
// THE SURFACE REUSES CVCRAFT'S EXISTING VISUAL SYSTEM VERBATIM. Every value below is a `var(--token)` reference
// into `client/src/index.css`'s `:root`, which `designTokens.test.tsx` guards. No new palette value, no new type
// step, no Tailwind. The one thing this file adds is a CONTRACT, not a look:
//
//   THE UNIFORM-BAND CONTRACT (§1.4, T-UNIFORM-1). Every band value — on every facet, domain, sub-element, and
//   overlay documentation class — renders with identical colour, identical font weight, identical font size,
//   identical chip treatment, and identical icon treatment (which is: no icon). "Substantial" is typographically
//   indistinguishable from "Not shown" except in its label text.
//
//   This is enforced STRUCTURALLY, by there being exactly one frozen style object that every band chip shares by
//   reference, and exactly one component (`BandChip`) that may use it. There is no band→style map to keep honest,
//   no per-value modifier class to forget to delete, and nothing for a colour to be keyed off. §12.4 additionally
//   bans traffic-light semantics, checkmark/cross iconography, and filled/unfilled dot ladders outright — the
//   absence of an icon slot in `BandChip` is what makes that unreachable rather than merely undone.
import type { CSSProperties } from 'react';

/** The ONE band-chip treatment. Frozen: a component that tried to spread-and-tweak it fails at the type level. */
export const BAND_CHIP_STYLE: Readonly<CSSProperties> = Object.freeze({
  display: 'inline-block',
  padding: 'var(--space-1) var(--space-2)',
  border: '1px solid var(--rule)',
  borderRadius: '4px',
  background: 'var(--surface-subtle)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-label)',
  fontWeight: 'var(--fw-medium)',
  fontStyle: 'normal',
  letterSpacing: 'normal',
  textTransform: 'none',
  textDecoration: 'none',
  lineHeight: 'var(--lh-body)',
});

/** Section shells. Panels, not cards-with-emphasis: no section is visually louder than another. */
export const SECTION_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-6)',
  padding: '0',
});

export const SECTION_HEADING_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-3)',
  fontFamily: 'var(--serif)',
  fontSize: 'var(--fs-h2)',
  lineHeight: 'var(--lh-snug)',
  color: 'var(--ink)',
});

export const CARD_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-3)',
  padding: 'var(--space-4)',
  background: 'var(--card)',
  border: '1px solid var(--rule)',
  borderRadius: '6px',
  boxShadow: 'var(--shadow-soft)',
});

/* ============================================================================================================== *
 * R5.7 — THE LEGACY READ'S CHROME, CARRIED OVER (owner direction 2026-08-22: "the display and format of the
 * previous version was very okay — it is what we will keep; the rebuild is mostly the engine").
 *
 * These values are `QualityReadView`'s own `S.card` / `S.cardHead` / `S.roleTitle` / `S.sectionRec` / `S.caution`
 * / `S.paneHead`, restated here as frozen constants so the R5 surface presents the way the legacy read did without
 * importing a module §8.4 retires. PRESENTATION ONLY: no rating ink, no `ratingInk()`, no verdict colours — a band
 * still renders through the ONE `BAND_CHIP_STYLE` above, and nothing here keys a colour off a value.
 * ============================================================================================================== */

/** The legacy section card, now the entry card and the compact summary card. (`S.card`, padding folded in.) */
export const ENTRY_CARD_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: 'var(--space-2) 0',
  padding: 'var(--space-3) var(--space-4) var(--space-4)',
  background: 'var(--card)',
  border: '1px solid var(--rule)',
  borderRadius: '8px',
});

/** The card's head row: identity on the left, its band (or state) on the right. (`S.cardHead`, as a div.) */
export const CARD_HEAD_STYLE: Readonly<CSSProperties> = Object.freeze({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  flexWrap: 'wrap',
  margin: '0 0 var(--space-2)',
});

/** The entry's identity line — the legacy `S.roleTitle`: sans, semibold, body size. */
export const ENTRY_TITLE_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: 0,
  fontFamily: 'var(--sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-body)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

/** A domain's name on its summary card — the legacy `S.secName`. */
export const SUMMARY_NAME_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: 0,
  fontFamily: 'var(--sans)',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

/** A sub-block label inside a card — the legacy `S.paneHead`: small caps, muted. NOT a heading element. */
export const SUB_LABEL_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: 'var(--space-3) 0 var(--space-1)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--muted)',
});

/** The facets row — three label/chip pairs on one line, wrapping on narrow viewports. */
export const FACET_ROW_STYLE: Readonly<CSSProperties> = Object.freeze({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2) var(--space-4)',
  margin: '0 0 var(--space-2)',
});

/**
 * The recommendation item. THE SAME OBJECT for every recommendation, of every kind, at every position —
 * §1.4's "no priority, focus, strongest/weakest, or emphasis styling of any recommendation relative to another".
 * The look is the legacy `S.sectionRec` (ink left rule on the subtle surface).
 */
export const REC_CARD_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderLeft: '3px solid var(--ink)',
  background: 'var(--surface-subtle)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-body)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

/** A flag ("point to check") — the legacy `S.caution` in its NEUTRAL form: rule-coloured edge, never warning ink. */
export const FLAG_ITEM_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  borderLeft: '3px solid var(--rule)',
  background: 'var(--surface-subtle)',
  borderRadius: '4px',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-small)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

/** The per-entry facts disclosure — the legacy `S.detail` (a bordered on-demand block). */
export const FACTS_DETAILS_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: 'var(--space-3) 0 0',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--rule)',
  borderRadius: '6px',
  background: 'var(--card)',
});

export const FACTS_SUMMARY_STYLE: Readonly<CSSProperties> = Object.freeze({
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-small)',
  fontWeight: 600,
  color: 'var(--ink-subtle)',
});

export const BODY_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-2)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-body)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

export const MUTED_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-2)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-small)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--muted)',
});

/** The neutral callout — flags, notices, banners. NEVER warning-red and never a fault icon (§4.7). */
export const NOTICE_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-3)',
  padding: 'var(--space-3)',
  background: 'var(--surface-subtle)',
  border: '1px solid var(--rule)',
  borderRadius: '6px',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-body)',
  lineHeight: 'var(--lh-body)',
  color: 'var(--ink)',
});

export const STICKY_HEADER_STYLE: Readonly<CSSProperties> = Object.freeze({
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  alignItems: 'baseline',
  padding: 'var(--space-2) var(--space-4)',
  background: 'var(--canvas)',
  borderBottom: '1px solid var(--rule)',
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-small)',
  color: 'var(--ink-subtle)',
});

export const EVIDENCE_SUMMARY_STYLE: Readonly<CSSProperties> = Object.freeze({
  fontFamily: 'var(--sans)',
  fontSize: 'var(--fs-small)',
  color: 'var(--ink-subtle)',
});

export const EVIDENCE_QUOTE_STYLE: Readonly<CSSProperties> = Object.freeze({
  margin: '0 0 var(--space-1)',
  padding: 'var(--space-1) var(--space-2)',
  borderLeft: '2px solid var(--rule)',
  fontFamily: 'var(--serif)',
  fontSize: 'var(--fs-small)',
  lineHeight: 'var(--lh-prose)',
  color: 'var(--ink-subtle)',
});

export const PAGE_STYLE: Readonly<CSSProperties> = Object.freeze({
  maxWidth: 'min(960px, 92vw)',
  margin: '0 auto',
  padding: '0 var(--space-4) var(--space-8)',
  fontFamily: 'var(--sans)',
  color: 'var(--ink)',
});

/** Visually hidden, still announced. The standard clip-rect pattern; no `display:none`. */
export const SR_ONLY: Readonly<CSSProperties> = Object.freeze({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
